import React, {useCallback, useEffect, useMemo, useState} from "react";
import {CollateralAnalyticsSearchParams, PopulationDensityCategory, Subject} from "../../../../api/ApiTypes.ts";
import { GoogleMapWithMarkers } from "../../../google/GoogleMapWithMarkers.tsx";
import { SimpleSamSelect } from "../../../SimpleSamSelect.tsx";
import {
    CompChangedResponse,
    COMPS_SEARCH_OPTIONS,
    SelectableComp, SelectedComp,
} from "./configurableCompsSelectTypes.tsx";
import SamButton from "../../../SamButton.tsx";
import { Search } from "lucide-react";
import { ComparablesTableSkeleton } from "./ComparablesTableSkeleton.tsx";
import { CompsDragDropTable } from "./CompsDragDropTable.tsx";
import {ConfigurableCompsSelectComponent} from "../../DynamicFormTypes.ts";
import {useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {useWatch} from "react-hook-form";
import {defaultParserForSelects} from "../../dyanimcFormUtil.tsx";

interface CompsMapSearchV2Props {
    selectableComps: SelectableComp[];
    subject?: Subject;
    selectedComps: SelectedComp[];
    setSelectedCompAtRow: (comp: SelectableComp, slot: SelectedComp) => Promise<CompChangedResponse>;
    compDefinition: ConfigurableCompsSelectComponent;
    initialSearchParams: CollateralAnalyticsSearchParams;
    setSearchParams: React.Dispatch<React.SetStateAction<CollateralAnalyticsSearchParams>>;
    latitude: number,
    longitude: number,
    isLoading: boolean;
    getSelectedCompByMlsIdOrAddress: (address: string | undefined, mlsId: string | undefined) => SelectedComp | undefined;
    compareMlsIdsOrAddressesMatch: (address1: string, address2: string, mlsId1: string | undefined, mlsId2: string | undefined) => boolean;
    handleClickComp: (comp: SelectableComp) => void;
    handleRemoveComp: (selected: SelectedComp) => void;
}

const CompsMapSearchV2Renderer: React.FC<CompsMapSearchV2Props> = ({
                                                                       selectableComps,
                                                                       subject,
                                                                       latitude,
                                                                       longitude,
                                                                       selectedComps,
                                                                       setSelectedCompAtRow,
                                                                       compDefinition,
                                                                       initialSearchParams,
                                                                       setSearchParams,
                                                                       isLoading,
                                                                       getSelectedCompByMlsIdOrAddress,
                                                                       compareMlsIdsOrAddressesMatch,
                                                                       handleClickComp,
                                                                       handleRemoveComp
                                                                             }) => {
    const { formMethods } = useFormMethods()
    const { metaData } = useFormStatic();


    const [localSearchParams, setLocalSearchParams] = useState<CollateralAnalyticsSearchParams>(initialSearchParams);

    const filteredMarkers = useMemo(() => {
        return selectableComps
            .filter(pv => pv.propertyValuation && pv.propertyValuation.latitude && pv.propertyValuation.longitude
                && pv.propertyValuation.address && !compareMlsIdsOrAddressesMatch(pv.propertyValuation.address, metaData.streetAddress, pv.propertyValuation.mlsId, undefined))
            .map(pv => ({
                latitude: pv.propertyValuation!.latitude!,
                longitude: pv.propertyValuation!.longitude!,
                address: pv.propertyValuation!.address!,
                compType: pv.propertyValuation?.compType,
                mlsId: pv.propertyValuation?.mlsId
            }));
    }, [selectableComps]);

    // Handles clicks on map pins by mapping the clicked marker back to its corresponding comparable,
    // then opening the details flyout.
    const onMarkerClick = useCallback((marker: { address: string; mlsId?: string }) => {
        const match = selectableComps.find(sc => {
            const propValuation = sc.propertyValuation;
            if (!propValuation) {
                return false;
            }

            return compareMlsIdsOrAddressesMatch(
                marker.address,
                propValuation.address ?? '',
                marker.mlsId,
                propValuation.mlsId
            );
        });

        if (match) {
            handleClickComp(match);
        }
        else {
            console.warn("Marker click could not be matched to a selectable comp:", marker);
        }
    }, [selectableComps, handleClickComp, compareMlsIdsOrAddressesMatch]);

    const updateSearchParam = useCallback((key: keyof CollateralAnalyticsSearchParams, value: any) => {
        setLocalSearchParams(prev => ({ ...prev, [key]: value }));
    }, []);

    const onSearch = () => {
        //this will trigger an actual search with the updated params
        setSearchParams(localSearchParams);
    };

    const [distanceOptions, setDistanceOptions] = useState(COMPS_SEARCH_OPTIONS.suburbanDistanceOptions);

    // Watch the specific field
    const watchedValue = useWatch({
        control: formMethods.control,
        name: "populationDensity"
    })

    useEffect(() => {
        // This effect will run whenever the watched field changes
        if (watchedValue !== undefined) {
            // Call the async function
            handleChange();
        }
    }, [watchedValue]);

    // Use useCallback to memoize the async function
    const handleChange = useCallback(async () => {
        const populationCategoryRaw = defaultParserForSelects(formMethods.getValues("populationDensity"));

        const populationCategory = populationCategoryRaw && populationCategoryRaw.value ? populationCategoryRaw.value : null;
        switch (populationCategory) {
            case PopulationDensityCategory.URBAN:
                setDistanceOptions(COMPS_SEARCH_OPTIONS.urbanDistanceOptions);
                break;
            case PopulationDensityCategory.RURAL:
                setDistanceOptions(COMPS_SEARCH_OPTIONS.ruralDistanceOptions);
                break;
            case PopulationDensityCategory.SUBURBAN:
                setDistanceOptions(COMPS_SEARCH_OPTIONS.suburbanDistanceOptions);
                break;
            default:
                setDistanceOptions(COMPS_SEARCH_OPTIONS.suburbanDistanceOptions);
                break;
        }
    }, []);



    const renderSelectField = (label: string, paramKey: keyof CollateralAnalyticsSearchParams, options: any[]) => (
        <SimpleSamSelect
            label={label}
            value={localSearchParams[paramKey] as string}
            onChange={(value) => updateSearchParam(paramKey, value)}
            options={options}
        />
    );

    const renderCheckbox = (id: string, label: string, paramKey: keyof CollateralAnalyticsSearchParams) => (
        <div className="flex items-center">
            <input
                type="checkbox"
                id={id}
                className="mr-2"
                checked={localSearchParams[paramKey] as boolean}
                onChange={(e) => updateSearchParam(paramKey, e.target.checked)}
            />
            <label htmlFor={id} className="text-sm">{label}</label>
        </div>
    );

    return (
        <>
            <GoogleMapWithMarkers
                lat={latitude}
                lng={longitude}
                markers={filteredMarkers}
                onMarkerClick={onMarkerClick}
                getSelectedCompByMlsIdOrAddress={getSelectedCompByMlsIdOrAddress}
            />

            <div className="w-full p-6 mt-2 border border-gray-300 rounded-lg shadow-md relative z-10">
                <h1 className="text-2xl font-semibold mb-6 text-blue-800">Market Data Search</h1>

                <div className="flex gap-8">
                    {/* Comparable Attributes Section */}
                    <div className="flex-1">
                        <h2 className="text-lg font-semibold mb-4 text-blue-800">Comparable Attributes</h2>
                        <div className="grid grid-cols-3 gap-4">
                            {renderSelectField("Gross Living Area", "grossLivingArea", COMPS_SEARCH_OPTIONS.grossLivingOptions)}
                            {renderSelectField("Bedrooms", "bedroomRange", COMPS_SEARCH_OPTIONS.bedroomOptions)}
                            {renderSelectField("Bathrooms", "bathroomRange", COMPS_SEARCH_OPTIONS.bathroomOptions)}
                            {renderSelectField("Lot Size", "lotSize", COMPS_SEARCH_OPTIONS.lotSizeOptions)}
                            {renderSelectField("Year Built", "yearBuiltRange", COMPS_SEARCH_OPTIONS.yearBuiltOptions)}
                            {renderSelectField("Property Type", "propertyType", COMPS_SEARCH_OPTIONS.propertyTypeOptions)}
                        </div>
                        <div className="flex items-center mt-4">
                            <input
                                type="checkbox"
                                id="matchWaterFront"
                                className="mr-2"
                                checked={localSearchParams.matchSubjectsWaterfront}
                                onChange={(e) => updateSearchParam("matchSubjectsWaterfront", e.target.checked)}
                            />
                            <label htmlFor="matchWaterFront" className="text-sm">
                                Match subject's waterfront characteristics
                            </label>
                        </div>
                    </div>

                    {/* Vertical Divider */}
                    <div className="border-l border-gray-300 mx-2" />

                    {/* Search Options Section */}
                    <div className="flex-1">
                        <h2 className="text-lg font-semibold mb-4 text-blue-800">Search Options</h2>
                        <div className="grid grid-cols-3">
                            {/* Checkboxes Column */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold mb-2">Include</h3>
                                <div className="grid grid-cols-1 gap-3">
                                    {renderCheckbox("searchSales", "Sales", "sales")}
                                    {renderCheckbox("searchListing", "Listings", "listings")}
                                    {renderCheckbox("searchRegular", "Regular", "regular")}
                                    {renderCheckbox("searchForclosure", "Foreclosure", "foreclosure")}
                                    {renderCheckbox("searchShortSales", "Short Sales", "shortSales")}
                                    {renderCheckbox("searchReo", "Reo", "reo")}
                                </div>
                            </div>

                            {/* Search Inputs Column */}
                            <div className="gap-4 grid grid-cols-2 col-span-2">
                                {renderSelectField("Date Range", "dateRange", COMPS_SEARCH_OPTIONS.dateRangeOptions)}
                                {renderSelectField("Search Type", "searchType", COMPS_SEARCH_OPTIONS.searchTypeOptions)}
                                {renderSelectField("Distance", "maxDistance", distanceOptions)}
                                {renderSelectField("Search Target", "searchTarget", COMPS_SEARCH_OPTIONS.searchTargetOptions)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <SamButton type="button" onClick={onSearch}>
                        <div className="flex items-center">
                            <Search className="mr-1.5" />
                            Search
                        </div>
                    </SamButton>
                </div>
            </div>

            <div>
                {isLoading ? (
                    <ComparablesTableSkeleton />
                ) : (
                    <CompsDragDropTable
                        componentDefinition={compDefinition}
                        selectableComps={selectableComps}
                        subject={subject}
                        selectedComps={selectedComps}
                        setSelectedCompAtRow={setSelectedCompAtRow}
                        handleClickComp={handleClickComp}
                        getSelectedCompByMlsIdOrAddress={getSelectedCompByMlsIdOrAddress}
                        handleRemoveComp={handleRemoveComp}
                    />
                )}
            </div>
        </>
    );
}

export const CompsMapSearchV2 = React.memo(CompsMapSearchV2Renderer);
